import {
  Button,
  Callout,
  Checkbox,
  DateRadioChipGroup,
  FormField,
  RadioChipGroup,
  RadioChipOptions,
} from '@expressable/syntax-ds';
import { PageSizeConfig } from 'components/CalendarPicker/CalendarPageConfig';
import { TimeSlotChipAdornment } from 'components/CalendarPicker/DateChipAdornment';
import { UseEvaluationCalendar } from 'domains/calendars/v3/useEvaluationCalendar';
import { UseEvaluationCalendarResult } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { FC, useMemo, useState, useEffect, useRef } from 'react';
import { useFormState, useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DATE_LONG_DAY_MONTH_ORDINAL } from 'utils/date';
import { RescheduleDirectFormData } from '../schema';
import { useEffectOnce } from 'react-use';
import { shouldDayBeDisabled } from '../utils/dateComparison';

export type RescheduleDirectCalendarPickerProps = {
  availability: UseEvaluationCalendar;
  onChangeTimeSlot: (timeSlot: Date | undefined) => void;
  onChangeDay: (day: number) => void;
  upcomingAppointmentDates: string[];
  originalAppointmentDate: string;
  careTeamPrimaryTherapistEmail: string;
  careTeamPrimaryTherapistName?: string;
  allowMultipleTreatingTherapists: boolean;
  showOnlyTimeSlots?: boolean; // Show only time slots (no day picker or checkbox)
  showOnlyDayPicker?: boolean; // Show only day picker (no checkbox, no time slots)
};

export const RescheduleDirectCalendarPicker: FC<RescheduleDirectCalendarPickerProps> = ({
  availability,
  onChangeTimeSlot,
  onChangeDay,
  upcomingAppointmentDates,
  originalAppointmentDate,
  careTeamPrimaryTherapistEmail,
  careTeamPrimaryTherapistName,
  allowMultipleTreatingTherapists,
  showOnlyTimeSlots = false,
  showOnlyDayPicker = false,
}) => {
  const { t } = useTranslation();
  const { isSubmitting, errors } = useFormState<RescheduleDirectFormData>();
  const { format } = useDisplayTimezone();
  const { register, setValue } = useFormContext<RescheduleDirectFormData>();
  // If multiple therapists not allowed, always show only current therapist
  const [showOnlyCurrentTherapist, setShowOnlyCurrentTherapist] = useState(!allowMultipleTreatingTherapists);
  
  // Sync checkbox state to form state so it can be accessed in parent component
  useEffect(() => {
    setValue('showOnlyCurrentTherapist', showOnlyCurrentTherapist);
  }, [showOnlyCurrentTherapist, setValue]);

  // @ts-expect-error -- TODO: fix type later
  const calendarData: Extract<UseEvaluationCalendarResult, { isLoading: false }> = availability;
  const selectedTimeSlot = useWatch({ name: 'timeSlot' }) as Date | undefined;

  const dayOptions: RadioChipOptions = useMemo(() => {
    return calendarData?.allTimeSlots
      .map(({ day, timeSlotCount, availabilityByTherapist }, index) => {
        // If filtering by current therapist, check if they have availability on this day
        let effectiveTimeSlotCount = timeSlotCount;
        if (showOnlyCurrentTherapist && careTeamPrimaryTherapistEmail) {
          const therapistAvailability = availabilityByTherapist.find(
            availability => availability.therapist.therapistID === careTeamPrimaryTherapistEmail,
          );
          effectiveTimeSlotCount = therapistAvailability ? therapistAvailability.timeSlots.length : 0;
        }

        const isDisabled = shouldDayBeDisabled(
          day,
          effectiveTimeSlotCount,
          originalAppointmentDate,
          upcomingAppointmentDates || [],
        );

        return {
          value: `${index}`,
          date: day,
          adornment: <TimeSlotChipAdornment count={effectiveTimeSlotCount} disabled={isDisabled} />,
          disabled: isDisabled,
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [
    availability,
    upcomingAppointmentDates,
    originalAppointmentDate,
    showOnlyCurrentTherapist,
    careTeamPrimaryTherapistEmail,
  ]);

  const timeSlots = useMemo(() => {
    if (!calendarData?.currentAvailability) return [];

    // If checkbox is enabled, use availabilityByTherapist to get times for specific therapist
    if (showOnlyCurrentTherapist && careTeamPrimaryTherapistEmail) {
      const therapistAvailability = calendarData.currentAvailability.availabilityByTherapist.find(
        availability => availability.therapist.therapistID === careTeamPrimaryTherapistEmail,
      );

      if (!therapistAvailability) return [];

      return therapistAvailability.timeSlots.map(slot => slot.time).sort((a, b) => a.getTime() - b.getTime());
    }

    // Otherwise, show all time slots
    return calendarData.currentAvailability.timeSlots.map(slot => slot.time).sort((a, b) => a.getTime() - b.getTime());
  }, [calendarData?.currentAvailability, showOnlyCurrentTherapist, careTeamPrimaryTherapistEmail]);

  const timeSlotOptions = useMemo(() => {
    return timeSlots.map((slot, index) => ({
      value: `${index}`,
      label: format(slot, 'h:mm a'),
    }));
  }, [timeSlots, format]);

  const selectedTimeSlotIndex = useMemo(() => {
    if (!selectedTimeSlot) return undefined;
    const index = timeSlots.findIndex(slot => slot.getTime() === selectedTimeSlot.getTime());
    return index >= 0 ? `${index}` : undefined;
  }, [selectedTimeSlot, timeSlots]);

  // Calculate initial startIndex synchronously so carousel shows correct page on first render
  // startIndex should be the POSITION in the array, not the value
  const initialStartIndex = useMemo(() => {
    const firstEnabledDayPosition = dayOptions.findIndex(option => !option.disabled);
    return firstEnabledDayPosition >= 0 ? firstEnabledDayPosition : 0;
  }, [dayOptions]);

  const hasInitialized = useRef(false);

  useEffectOnce(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    
    // Find the first non-disabled date and always call onChangeDay to update dayIndex state
    const firstEnabledDay = dayOptions.find(option => !option.disabled);
    if (firstEnabledDay && typeof firstEnabledDay.value === 'string') {
      onChangeDay(parseInt(firstEnabledDay.value, 10));
    } else {
      // No enabled day found - fall back to first day (index 0) but still call onChangeDay
      // to ensure dayIndex state is consistent with what's displayed
      onChangeDay(0);
    }
  });

  // Clear selected time slot if it's no longer in the available timeSlots
  // This handles the case when toggling the therapist filter
  useEffect(() => {
    if (selectedTimeSlot && timeSlots.length > 0) {
      const isSelectedSlotAvailable = timeSlots.some(slot => slot.getTime() === selectedTimeSlot.getTime());
      if (!isSelectedSlotAvailable) {
        onChangeTimeSlot(undefined);
        setValue('timeSlot', undefined);
      }
    } else if (selectedTimeSlot && timeSlots.length === 0) {
      // If no slots available, clear the selection
      onChangeTimeSlot(undefined);
      setValue('timeSlot', undefined);
    }
  }, [timeSlots, selectedTimeSlot, onChangeTimeSlot, setValue]);

  // If showOnlyTimeSlots is true, render only the time slot picker
  if (showOnlyTimeSlots) {
    return (
      <FormField
        name="timeSlot"
        label={t('rescheduleDirect.init.appointmentsOn', {
          date: format(calendarData.currentAvailability.day, 'EEE, MMM d'),
        })}
        hint={undefined}
        config={{ order: { error: 'bottom', hint: 'top' }, labelSpacing: 'lg' }}
        error={Boolean(errors.timeSlot?.message)}
        errorMessages={errors.timeSlot?.message ? [errors.timeSlot.message] : undefined}
      >
        {timeSlots.length === 0 ? (
          <Callout type="warning" className="mt-8">
            {t('rescheduleDirect.init.noTimesAvailableForDay')}
          </Callout>
        ) : (
          <RadioChipGroup
            htmlName="timeslot"
            chips={timeSlotOptions}
            variant="standard"
            onChange={e => {
              const pickedIndex = parseInt(e.target.value, 10);
              const slot = timeSlots[pickedIndex];
              if (slot) {
                onChangeTimeSlot(slot);
              }
            }}
            disabled={isSubmitting}
            value={selectedTimeSlotIndex ?? ''}
            standalone
            className="[&>*]:mobile-xs-d:grid-cols-3"
          />
        )}
        <input type="hidden" {...register('timeSlot')} />
      </FormField>
    );
  }

  // If showOnlyDayPicker is true, render only the day picker (no checkbox, no time slots)
  if (showOnlyDayPicker) {
    return (
      <FormField
        name="day"
        label={t('directSignup.schedule.dayPicker.label')}
        config={{ order: { error: 'top', hint: 'top' } }}
        error={!!errors.day}
        errorMessages={errors.day?.message ? [errors.day.message] : undefined}
      >
        <div id="reschedule-daypicker" tabIndex={-1} />
        <DateRadioChipGroup
          htmlName="daypicker"
          chips={dayOptions}
          variant="carousel"
          pageSize={PageSizeConfig}
          value={`${calendarData.dayIndex}`}
          startIndex={initialStartIndex}
          onChange={e => {
            onChangeTimeSlot(undefined);
            onChangeDay(parseInt(e.target.value, 10));
            setValue('slotId', undefined);
          }}
          disabled={isSubmitting}
          dateFormatFn={format}
          standalone
        />
        <input type="hidden" {...register('day')} />
      </FormField>
    );
  }

  return (
    <>
      <div className="mb-24 flex items-center gap-4">
        <Checkbox
          id="show-only-current-therapist"
          standalone
          variant="plain"
          size="sm"
          checked={showOnlyCurrentTherapist}
          disabled={!allowMultipleTreatingTherapists}
          onChange={e => {
            setShowOnlyCurrentTherapist(e.target.checked);
            // Clear the time slot selection when toggling the filter
            onChangeTimeSlot(undefined);
            setValue('timeSlot', undefined);
          }}
        />
        {t('rescheduleDirect.init.showOnlyCurrentTherapist')}
      </div>

      <FormField
        name="day"
        label={t('directSignup.schedule.dayPicker.label')}
        config={{ order: { error: 'top', hint: 'top' } }}
        error={!!errors.day}
        errorMessages={errors.day?.message ? [errors.day.message] : undefined}
      >
        <div id="reschedule-daypicker" tabIndex={-1} />
        <DateRadioChipGroup
          htmlName="daypicker"
          chips={dayOptions}
          variant="carousel"
          pageSize={PageSizeConfig}
          value={`${calendarData.dayIndex}`}
          startIndex={initialStartIndex}
          onChange={e => {
            onChangeTimeSlot(undefined);
            onChangeDay(parseInt(e.target.value, 10));
            setValue('slotId', undefined);
          }}
          disabled={isSubmitting}
          dateFormatFn={format}
          standalone
        />
        <input type="hidden" {...register('day')} />
      </FormField>

      {timeSlots.length === 0 && showOnlyCurrentTherapist ? (
        <div className="mb-40">
          <p className="text-24 font-medium leading-32 text-gray-900 mb-16">
            {t('rescheduleDirect.init.noAppointmentsWithTherapist', {
              date: format(calendarData.currentAvailability.day, 'EEEE, MMMM d'),
              therapistName: careTeamPrimaryTherapistName,
            })}
          </p>
          <p className="text-16 leading-24 text-gray-700">
            {t('rescheduleDirect.init.tryAnotherDate')}
            <Button
              variant="link"
              onClick={() => {
                setShowOnlyCurrentTherapist(false);
                onChangeTimeSlot(undefined);
                setValue('timeSlot', undefined);
              }}
            >
              {t('rescheduleDirect.init.uncheckFilter')}
            </Button>{' '}
            {t('rescheduleDirect.init.viewOtherTherapists')}
          </p>
        </div>
      ) : (
        <FormField
          name="timeSlot"
          label={t('rescheduleDirect.init.availableTimesOn', {
            date: format(calendarData.currentAvailability.day, DATE_LONG_DAY_MONTH_ORDINAL),
          })}
          hint={timeSlots.length > 0 ? t('rescheduleDirect.init.selectTime') : undefined}
          config={{ order: { error: 'bottom', hint: 'top' }, labelSpacing: 'lg' }}
          error={Boolean(errors.timeSlot?.message)}
          errorMessages={errors.timeSlot?.message ? [errors.timeSlot.message] : undefined}
        >
          {timeSlots.length === 0 ? (
            <Callout type="warning" className="mt-8">
              {t('rescheduleDirect.init.noTimesAvailableForDay')}
            </Callout>
          ) : (
            <RadioChipGroup
              htmlName="timeslot"
              chips={timeSlotOptions}
              variant="standard"
              onChange={e => {
                const pickedIndex = parseInt(e.target.value, 10);
                const slot = timeSlots[pickedIndex];
                if (slot) {
                  onChangeTimeSlot(slot);
                }
              }}
              disabled={isSubmitting}
              value={selectedTimeSlotIndex ?? ''}
              standalone
              className="[&>*]:mobile-xs-d:grid-cols-3"
            />
          )}
          <input type="hidden" {...register('timeSlot')} />
        </FormField>
      )}
    </>
  );
};
